import { Document, View, StyleSheet, Text } from '@react-pdf/renderer';
import React from 'react';
import dayjs from 'libs/dayjs';
import PageA4 from '../Elements/Pages/PageA4';
import Normal from '../Elements/Text/Normal';
import Title1 from '../Elements/Text/Title1';
import Footer from '../Footer/Footer';
import Header from '../Header/Header';
import SignatureBlock from 'PDF/Signatures';

const styles = StyleSheet.create({
  page: {
    paddingBottom: '4cm',
    paddingTop: '4cm',
    paddingHorizontal: '1.5cm',
    backgroundColor: '#ffffff',
    minHeight: '100vh',
  },
  section: {
    marginBottom: '1.5cm',
    padding: '0.8cm',
    backgroundColor: '#ffffff',
    borderRadius: '0.3cm',
    border: '1px solid #e8e8e8',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  body: {
    flex: 1,
    paddingTop: '0.5cm',
  },
  titleContainer: {
    fontSize: '18px',
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    backgroundColor: '#548235',
    paddingVertical: '0.6cm',
    paddingHorizontal: '1cm',
    textAlign: 'center',
    borderRadius: '0.3cm',
    marginBottom: '1cm',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  greeting: {
    fontSize: '16px',
    fontFamily: 'Helvetica-Bold',
    color: '#2c3e50',
    marginBottom: '0.8cm',
    textTransform: 'capitalize',
  },
  paragraph: {
    fontSize: '11px',
    fontFamily: 'Helvetica',
    color: '#34495e',
    lineHeight: 1.6,
    textAlign: 'justify',
    marginBottom: '0.8cm',
  },
  header: {
    width: '100vw',
    position: 'absolute',
    top: '0px',
    left: '0px',
    right: '0px',
    height: '3.5cm',
    backgroundColor: '#ffffff',
    borderBottom: '2px solid #548235',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: '1.5cm',
    paddingVertical: '0.4cm',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  footer: {
    position: 'absolute',
    bottom: '0px',
    left: '0px',
    right: '0px',
    width: '100vw',
    height: '3.5cm',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: '0.6cm',
    boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
  },
  // Estilos de tabla originales
  table: {
    display: 'table',
    width: '100%',
    marginTop: 0,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableColHeader: {
    width: '16.66%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    backgroundColor: '#f2f2f2',
    textAlign: 'center',
    padding: 5,
  },
  tableColObservationHeader: {
    width: '33.33%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    backgroundColor: '#44b3e1',
    textAlign: 'center',
    padding: 5,
  },
  tableColSuggestionHeader: {
    width: '33.33%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    backgroundColor: '#ffff00',
    textAlign: 'center',
    padding: 5,
  },
  tableCol: {
    width: '16.66%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    textAlign: 'center',
    padding: 5,
  },
  tableColObservation: {
    width: '33.33%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    textAlign: 'center',
    padding: 5,
  },
  tableColSuggestion: {
    width: '33.33%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    textAlign: 'center',
    padding: 5,
  },
  tableCellHeader: {
    margin: 'auto',
    fontSize: 12,
    fontWeight: 'bold',
  },
  tableCell: {
    margin: 'auto',
    fontSize: 10,
  },
  tableRowNA: {
    backgroundColor: '#fff3cd',
  },
  tableRowExitoso: {
    backgroundColor: '#d4edda',
  },
  tableRowRechazado: {
    backgroundColor: '#f8d7da',
  },
  // Estilos para encabezados de actividad originales
  activityHeader: {
    flexDirection: 'row',
    backgroundColor: '#001f5b',
    color: '#fff',
    marginTop: 0,
  },
  activitySubHeader: {
    flexDirection: 'row',
    backgroundColor: '#c0e6f5',
    color: '#2c3e50',
    marginBottom: '0.3cm',
  },
  headerColCategory: {
    width: '20%',
    textAlign: 'left',
    padding: 5,
    fontSize: 12,
  },
  headerColActivity: {
    width: '80%',
    textAlign: 'left',
    padding: 5,
    fontSize: 12,
  },
});

function MantencionDocument({ mantencion: data, actividades, config, staticData }) {
  const { Cliente, Responsable, ...mantencion } = data;

  const getTextByKey = (key) => {
    const entry = staticData?.find(
      (item) => item.keyName === key && item.documentType === 'mantencion'
    );
    return entry ? entry.textValue : '';
  };

  const formatText = (text) => {
    return text
      .replace(/{client_name}/g, Cliente.name)
      .replace(/{client_address}/g, Cliente.address)
      .replace(/{client_commune}/g, Cliente.commune)
      .replace(/{date}/g, dayjs(mantencion.createdAt).format('DD [de] MMMM [del] YYYY'));
  };

  const renderTableHeader = () => (
    <View style={styles.tableRow}>
      <View style={styles.tableColHeader}>
        <Text style={styles.tableCellHeader}>Ascensores</Text>
      </View>
      <View style={styles.tableColHeader}>
        <Text style={styles.tableCellHeader}>Estado</Text>
      </View>
      <View style={styles.tableColObservationHeader}>
        <Text style={styles.tableCellHeader}>Observación</Text>
      </View>
      <View style={styles.tableColSuggestionHeader}>
        <Text style={styles.tableCellHeader}>Sugerencia</Text>
      </View>
    </View>
  );

  const getRowStyle = (estado) => {
    switch (estado) {
      case 'NA':
        return styles.tableRowNA;
      case 'EXITOSO':
        return styles.tableRowExitoso;
      case 'RECHAZADO':
        return styles.tableRowRechazado;
      default:
        return {};
    }
  };

  const renderTableRow = (respuesta) => (
    <View key={respuesta.id} style={[styles.tableRow, getRowStyle(respuesta.estado)]} wrap={false}>
      <View style={styles.tableCol}>
        <Text style={styles.tableCell}>{respuesta.Ascensor.nombre}</Text>
      </View>
      <View style={styles.tableCol}>
        <Text style={{ ...styles.tableCell, fontSize: 8 }}>
          {respuesta.estado === 'EXITOSO'
            ? 'FUNCIONANDO'
            : respuesta.estado === 'RECHAZADO'
            ? 'FALLA'
            : 'NO APLICA'}
        </Text>
      </View>
      <View style={styles.tableColObservation}>
        <Text style={styles.tableCell}>
          {respuesta.estado === 'RECHAZADO' &&
          (!respuesta.observacion || respuesta.observacion.trim() === '')
            ? 'Con problemas'
            : respuesta.estado === 'EXITOSO' &&
              (!respuesta.observacion || respuesta.observacion.trim() === '')
            ? 'Estado normal'
            : respuesta.observacion}
        </Text>
      </View>
      <View style={styles.tableColSuggestion}>
        <Text style={styles.tableCell}>
          {!respuesta.sugerencia || respuesta.sugerencia === '' || respuesta.sugerencia === null
            ? 'Sin sugerencias'
            : respuesta.sugerencia}
        </Text>
      </View>
    </View>
  );

  return (
    <Document>
      <PageA4 style={styles.page}>
        <View style={styles.header} fixed>
          <Header
            title={`Informe de Mantención N°${mantencion.id}`}
            config={config}
            date={mantencion.createdAt}
          />
        </View>

        {/* Contenido principal */}
        <View style={styles.body}>
          {/* Sección 1: Saludo y Introducción */}
          <View style={styles.section}>
            <Text style={styles.greeting}>{formatText(getTextByKey('greeting'))}</Text>
            <Text style={styles.paragraph}>{formatText(getTextByKey('introduction'))}</Text>
            <Text style={styles.paragraph}>{getTextByKey('report_includes')}</Text>
          </View>

          {/* Sección 2: Informe de Actividades */}
          <View style={styles.section}>
            <View style={styles.titleContainer}>
              <Text>{getTextByKey('report_title')}</Text>
            </View>
            <Text style={styles.paragraph}>{getTextByKey('following')}</Text>

            {actividades
              ?.filter((actividad) =>
                actividad.Respuestas.some(
                  (respuesta) => respuesta.estado && respuesta.estado !== 'NA'
                )
              )
              .map((actividad, index) => (
                <View
                  key={actividad.id}
                  style={{ marginTop: index === 0 ? '0.5cm' : '1cm' }}
                  wrap={false}
                >
                  <View style={styles.activityHeader}>
                    <Text
                      style={{
                        ...styles.headerColCategory,
                        textAlign: 'center',
                      }}
                    >
                      Sector
                    </Text>
                    <Text
                      style={{
                        ...styles.headerColActivity,
                        textAlign: 'center',
                      }}
                    >
                      Actividad
                    </Text>
                  </View>
                  <View
                    style={{
                      ...styles.activityHeader,
                      backgroundColor: '#c0e6f5',
                    }}
                  >
                    <Text
                      style={{
                        ...styles.headerColCategory,
                        color: 'black',
                        textAlign: 'center',
                      }}
                    >
                      {actividad.Categoria.nombre}
                    </Text>
                    <Text style={{ ...styles.headerColActivity, color: 'black' }}>
                      {actividad.actividad}
                    </Text>
                  </View>
                  <View style={styles.table}>
                    {renderTableHeader()}
                    {actividad.Respuestas.filter(
                      (respuesta) => respuesta.estado && respuesta.estado !== 'NA'
                    )?.map((respuesta) => renderTableRow(respuesta))}
                  </View>
                </View>
              ))}
          </View>

          {/* Sección 3: Observaciones Generales */}
          <View style={styles.section}>
            <View style={styles.titleContainer}>
              <Text>Observaciones Generales</Text>
            </View>
            <Text style={styles.paragraph}>
              {mantencion.observaciones ?? 'SIN OBSERVACIONES GENERALES'}
            </Text>
          </View>

          {/* Sección 4: Información de Entrega */}
          <View style={[styles.section]} wrap={false}>
            <View style={styles.titleContainer}>
              <Text>Información de Entrega</Text>
            </View>
            <Text style={styles.paragraph}>
              A continuación, se encuentra la firma, la información del especialista y de quien
              recibe la atención. Esto, cuenta como una constancia firmada de que se realizó la
              atención.
            </Text>
            <SignatureBlock
              specialistSignature={mantencion.firma_spec}
              recepcionistName={mantencion.name_recep}
              recepcionistSignature={mantencion.firma_recep}
              recepcionistCharge={mantencion.cargo_recep}
              specialistName={Responsable.fullname}
            />
          </View>
        </View>

        {/* Footer fijo */}
        <View style={styles.footer} fixed>
          <Footer config={config} />
        </View>
      </PageA4>
    </Document>
  );
}

export default MantencionDocument;
